import classNames from "classnames";
import { BracketSeries } from "@portal-frontend-ssr/blast-api";
import { generateTeamLogoSrc } from "~/shared/helpers/url/generateTeamLogoSrc";
import { useCallback, useMemo } from "react";
import { generateMatchPageUrl } from "~/shared/helpers/url/generateMatchPageUrl";
import { GameId } from "@portal-frontend-ssr/blast-api";
import { AdaptiveLink, useBreakpoints } from "@portal-frontend-ssr/ui";
import { MatchPageLinkWrapper } from "~/components/MatchPageLinkWrapper";
import { TeamSlot } from "~/routes/_cs.cs.bounty-simulator._index/components/TeamDropdown";

interface TeamProps {
  team: BracketSeries["teamA"] | BracketSeries["teamB"];
  pickedResults?: Record<string, TeamSlot>;
  match: BracketSeries;
  onTeamClick?: (match: BracketSeries, team: TeamSlot) => void;
  activeTeamId?: string;
  updateActiveTeamId?: (uuid?: string) => void;
  score?: number;
}

function Team({ team, pickedResults, match, onTeamClick, activeTeamId, updateActiveTeamId, score }: TeamProps) {
  const teamSlot = team?.uuid === match.teamA?.uuid ? TeamSlot.A : TeamSlot.B;
  const state: "picked" | "not-picked" | "picked-incorrect" | "picked-correct" = useMemo(() => {
    if (!pickedResults?.[match.uuid]) return "not-picked";
    const isThisPicked = pickedResults[match.uuid] === teamSlot;
    if (match.isCompleted) {
      const winnerTeam = match.teamAScore > match.teamBScore ? TeamSlot.A : TeamSlot.B;

      // If the teamslot is the same as the winner, it's picked correctly
      if (isThisPicked && teamSlot === winnerTeam) return "picked-correct";

      if (isThisPicked && teamSlot !== winnerTeam) return "picked-incorrect";

      if (!isThisPicked && teamSlot === winnerTeam) return "picked-incorrect";

      if (!isThisPicked && teamSlot !== winnerTeam) return "picked-correct";

      return "not-picked";
    }
    if (isThisPicked) return "picked";

    return "not-picked";
  }, [match.isCompleted, match.teamAScore, match.teamBScore, match.uuid, pickedResults, teamSlot]);
  return (
    <button
      className={classNames(
        "flex h-8 w-full items-center justify-between gap-2 rounded-small border bg-background-100 p-2",
        {
          "border-primary-100": team && activeTeamId === team?.uuid,
          "border-success bg-success/20": state === "picked-correct" || state === "picked",
          "border-background-85": state === "not-picked",
          "border-error bg-error/20": state === "picked-incorrect",
        },
      )}
      onClick={() => onTeamClick?.(match, teamSlot)}
      onMouseEnter={() => updateActiveTeamId?.(team?.uuid)}
    >
      <div className="flex flex-row items-center gap-2">
        {team?.uuid && (
          <img
            className="size-4 object-contain"
            src={generateTeamLogoSrc({ id: team.uuid, width: 32 })}
            alt={team.shorthand}
          />
        )}
        {team?.name !== undefined ? (
          <span className="truncate">{team?.name}</span>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <div className="flex size-4 items-center justify-center rounded-full bg-background-80">
              <span className="text-white/90 font-style-label-l4">?</span>
            </div>
            <span className="text-center uppercase text-white font-style-body-b4">TBD</span>
          </div>
        )}
      </div>
      {team && !match.isCompleted && !match.isLive && <span>-</span>}
      {team && (match.isCompleted || match.isLive) && <span>{score}</span>}
    </button>
  );
}
export const FlowBracketMatch = ({
  gameId,
  tournamentName,
  match,
  activeTeamId,
  onTeamHover,
  onTeamClick,
  pickedResults,
}: {
  gameId: GameId;
  tournamentName?: string;
  match: BracketSeries;
  activeTeamId?: string;
  pickedResults?: Record<string, TeamSlot>;
  onTeamHover: (uuid?: string) => void;
  onTeamClick?: (match: BracketSeries, team: TeamSlot) => void;
}) => {
  const { isMobile } = useBreakpoints();

  const teams = [
    match.teamA ? { ...match.teamA, score: match.teamAScore } : null,
    match.teamB ? { ...match.teamB, score: match.teamBScore } : null,
  ];

  const updateActiveTeamId = useCallback(
    (newActiveTeamId?: string) => {
      if (newActiveTeamId === undefined) return;
      onTeamHover(newActiveTeamId);
    },
    [onTeamHover],
  );

  const matchPageLink = generateMatchPageUrl({
    game: gameId,
    tournamentName,
    matchName: match.name,
    matchUuid: match.uuid,
    teamAName: match.teamA?.shorthand,
    teamBName: match.teamB?.shorthand,
  });
  if (onTeamClick) {
    return (
      <div className={classNames("relative w-[148px] select-none")}>
        {match.isLive && (
          <div className="absolute -right-0 rounded-small bg-error px-1 uppercase text-white font-style-label-l4">
            live
          </div>
        )}
        <h3 className="truncate text-start text-neutral font-style-label-l4" title={match?.name}>
          {match?.name}
        </h3>
        <div className="mt-1 flex flex-col gap-px rounded-small border-[3px] border-background-85 bg-background-100 font-style-body-b4">
          {teams.map((team, index) => (
            <Team
              key={index}
              team={team}
              pickedResults={pickedResults}
              match={match}
              onTeamClick={onTeamClick}
              activeTeamId={activeTeamId}
              updateActiveTeamId={updateActiveTeamId}
              score={team?.score}
            />
          ))}
        </div>
      </div>
    );
  }
  return (
    <MatchPageLinkWrapper
      condition={!isMobile && matchPageLink !== undefined}
      wrapper={(children) => <AdaptiveLink to={matchPageLink ?? ""}>{children}</AdaptiveLink>}
    >
      <div className={classNames("relative w-[148px] select-none")}>
        {match.isLive && (
          <div className="absolute -right-0 rounded-small bg-error px-1 uppercase text-white font-style-label-l4">
            live
          </div>
        )}
        <h3 className="truncate text-start text-neutral font-style-label-l4" title={match?.name}>
          {match?.name}
        </h3>
        <div className="mt-1 flex flex-col gap-px rounded-small border-[3px] border-background-85 bg-background-85 font-style-body-b4">
          {teams.map((team, index) => (
            <Team
              key={index}
              team={team}
              pickedResults={pickedResults}
              match={match}
              onTeamClick={onTeamClick}
              activeTeamId={activeTeamId}
              updateActiveTeamId={updateActiveTeamId}
              score={team?.score}
            />
          ))}
        </div>
      </div>
    </MatchPageLinkWrapper>
  );
};
